import React, {
  useMemo,
  Dispatch,
  SetStateAction,
} from 'react';
import { useSelector } from 'react-redux';
import { isAccessControlModeActive as isAccessControlModeActiveSelector } from 'services/admin/selectors';
import { TITLE, SUBTITLE, DATE_TIME } from 'injection-classes';
import GateTextSection from '../SharedComponents/GateTextSection';
import {
  getTextColorContent,
} from 'services/app/selectors';
import { DEFAULT_GATE_TITLE, DEFAULT_GATE_SUBTITLE, GATE_SUBTITLE_MAX_LENGTH, GATE_TITLE_MAX_LENGTH } from 'shared/constants';
import useEditGate from 'hooks/use-edit-gate';
import DateAndTime from '../SharedComponents/DateAndTime';
import { AccessSectionContainer, ClickableEditButton } from '../styles';
import DateAndTimeModal from '../Modals/DateAndTimeModal';
import { MAESTRO_WHITE } from 'style/constants';
import { getDefaultRichTextEditorData } from 'utils';
import { getAdminText400 } from 'services/themes';
import { useAdminTranslation } from 'hooks/use-translation';
import { PREDEFINED_TOOLBAR_BUTTONS } from 'components/ui/RichTextEditorV2/utils';

interface IAccessInformation {
  isDateAndTimeModalVisible: boolean;
  setIsDateAndTimeModalVisible: Dispatch<SetStateAction<boolean>>;
}

const AccessInformation: React.FC<IAccessInformation> = ({
  isDateAndTimeModalVisible,
  setIsDateAndTimeModalVisible,
}) => {
  const { t } = useAdminTranslation();
  const contentTextColor = useSelector(getTextColorContent);
  const adminText400 = useSelector(getAdminText400);
  const isAccessControlModeActive = useSelector(
    isAccessControlModeActiveSelector,
  );
  const {
    editingGateInformation,
    setGateTitleChanges,
    setGateSubtitleChanges,
    setGateDateAndTime,
  } = useEditGate();

  const currentTime = Date.now();
  const timeStamp = editingGateInformation?.subscriptionTimestamp || currentTime;
  const isDateAndTimeEnabled = Boolean(editingGateInformation?.subscriptionTimestamp);

  const titleData = useMemo(() => {
    // Styled Data
    if (editingGateInformation?.titleRawData) {
      return editingGateInformation.titleRawData;
    }
    // Default Data
    // This section handles initializations and legacy data.
    const defaultTextTitle =
      editingGateInformation?.title ||
      editingGateInformation?.subscriptionTitle ||
      DEFAULT_GATE_TITLE(t);
    return getDefaultRichTextEditorData({
      text: defaultTextTitle,
      color:
        defaultTextTitle === DEFAULT_GATE_TITLE(t)
          ? MAESTRO_WHITE
          : contentTextColor,
      fontSize: 30,
    });
  }, [
    editingGateInformation?.title,
    editingGateInformation?.titleRawData,
    editingGateInformation?.subscriptionTitle,
    t,
  ]);

  const subtitleData = useMemo(() => {
    // Styled Data
    if (editingGateInformation?.subtitleRawData) {
      return editingGateInformation.subtitleRawData;
    }
    // Dafault Data
    const defaultTextSubtitle =
      editingGateInformation?.subtitle ||
      editingGateInformation?.subscriptionSubtitle ||
      DEFAULT_GATE_SUBTITLE(t);
    return getDefaultRichTextEditorData({
      text: defaultTextSubtitle,
      color:
        defaultTextSubtitle === DEFAULT_GATE_SUBTITLE(t)
          ? adminText400
          : contentTextColor,
      fontSize: 14,
    });
  }, [
    editingGateInformation?.subtitle,
    editingGateInformation?.subscriptionSubtitle,
    editingGateInformation?.subtitleRawData,
    t,
  ]);

  const handleTitleChange = (styledTitle: string) => {
    if (isAccessControlModeActive) {
      setGateTitleChanges(styledTitle);
    }
  };

  const handleSubtitleChange = (styledSubtitle: string) => {
    if (isAccessControlModeActive) {
      setGateSubtitleChanges(styledSubtitle);
    }
  };

  const toggleDateAndTimeModal = (
    e: React.MouseEvent<HTMLDivElement, MouseEvent>,
  ) => {
    setIsDateAndTimeModalVisible(!isDateAndTimeModalVisible);
    e.stopPropagation();
  };

  const closeDateAndTimeModal = () => {
    setIsDateAndTimeModalVisible(false);
  };

  const handleTimeStampUpdate = (
    newTimestamp: number | null,
    isDateAndTimeActive: boolean,
  ) => {
    const dateAndTime = isDateAndTimeActive ? newTimestamp : null;
    setGateDateAndTime(dateAndTime);
  };


  return (
    <>
      <AccessSectionContainer
        data-testid="dataAndTimeButton"
        sectionTitle={t('ACCESS_GATE_DATE_AND_TIME_REGION_TITLE')}
        isAccessControlMode={isAccessControlModeActive}
        width="fit-content"
      >
        {isAccessControlModeActive && (
          <>
            <ClickableEditButton onClick={toggleDateAndTimeModal} />
            {isDateAndTimeModalVisible && (
              <DateAndTimeModal
                timestamp={timeStamp}
                handleTimeStampChange={handleTimeStampUpdate}
                closeModal={closeDateAndTimeModal}
                isActive={isDateAndTimeEnabled}
              />
            )}
          </>
        )}
        <DateAndTime
          className={DATE_TIME}
          timeStamp={timeStamp}
          isFaded={!isDateAndTimeEnabled}
        />
      </AccessSectionContainer>
      <GateTextSection
        codeInjectionClassName={TITLE}
        isAccessControlMode={isAccessControlModeActive}
        onChange={handleTitleChange}
        data={titleData}
        sectionTitle={t('ACCESS_GATE_TITLE_REGION_TITLE')}
        type="title"
        maxLength={GATE_TITLE_MAX_LENGTH}
        toolbarButtons={PREDEFINED_TOOLBAR_BUTTONS.titleEditor}
      />
      <GateTextSection
        codeInjectionClassName={SUBTITLE}
        isAccessControlMode={isAccessControlModeActive}
        onChange={handleSubtitleChange}
        data={subtitleData}
        sectionTitle={t('ACCESS_GATE_SUBTITLE_REGION_TITLE')}
        type="subtitle"
        maxLength={GATE_SUBTITLE_MAX_LENGTH}
        toolbarButtons={PREDEFINED_TOOLBAR_BUTTONS.bodyEditor}
      />
    </>
  );
};

export default AccessInformation;
